import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { formatSalary, employmentLabel } from "@/lib/jobs";
import { ChevronDown, ChevronUp, BadgeCheck, MessageCircle, CheckCircle2, XCircle, Star, Eye, Loader2, Briefcase, Plus, Flame } from "lucide-react";
import ApplicationChat from "@/components/ApplicationChat";

export default function JobsMine() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState(null);
  const [open, setOpen] = useState(null);
  const [appsByJob, setAppsByJob] = useState({});
  const [err, setErr] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/jobs/mine"); setJobs(data || []); }
    catch (e) { setErr(formatErr(e)); }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    // Auto-open hash if present (#<jobId>)
    if (window.location.hash && jobs) {
      const id = window.location.hash.slice(1);
      if (jobs.find(j => j.id === id)) setOpen(id);
    }
  }, [jobs]);

  const expand = async (j) => {
    if (open === j.id) { setOpen(null); return; }
    setOpen(j.id);
    if (!appsByJob[j.id]) {
      try {
        const { data } = await api.get(`/jobs/${j.id}/applications`);
        setAppsByJob(prev => ({ ...prev, [j.id]: data || [] }));
      } catch (e) { setErr(formatErr(e)); }
    }
  };

  const setStatus = async (appId, status, jobId) => {
    try {
      await api.patch(`/jobs/applications/${appId}`, { status });
      setAppsByJob(prev => ({
        ...prev,
        [jobId]: (prev[jobId] || []).map(a => a.id === appId ? { ...a, status } : a),
      }));
    } catch (e) { setErr(formatErr(e)); }
  };

  if (!user) return <div className="max-w-md mx-auto p-10"><p>Please sign in.</p></div>;

  return (
    <div className="max-w-4xl mx-auto px-6 sm:px-8 py-10" data-testid="jobs-mine-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="overline">Hiring · Your posts</p>
          <h1 className="h-display text-4xl">Your job posts.</h1>
        </div>
        <Link to="/jobs/new" className="btn-primary" data-testid="post-job-cta">
          <Plus size={14}/> Post a job
        </Link>
      </div>
      {err && <p className="text-sm text-[#B84B4B] mt-3">{err}</p>}

      <div className="space-y-3 mt-7" data-testid="jobs-mine-list">
        {jobs === null && <p className="text-[#8A8A8A]">Loading…</p>}
        {jobs && jobs.length === 0 && (
          <div className="card p-8 text-center">
            <Briefcase size={26} className="mx-auto text-[#0057FF] mb-2"/>
            <p className="text-[#4A4A4A]">You haven&apos;t posted any jobs yet.</p>
          </div>
        )}
        {jobs && jobs.map(j => (
          <div key={j.id} className="card overflow-hidden" data-testid={`mine-job-${j.id}`} id={j.id}>
            <button onClick={()=>expand(j)} className="w-full text-left p-5 flex items-start gap-3 hover:bg-[#F2F2F0]/40 transition-colors"
                    data-testid={`mine-job-toggle-${j.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center flex-wrap gap-1.5">
                  <h3 className="font-medium text-lg">{j.title}</h3>
                  {j.is_boosted && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#0057FF] text-white inline-flex items-center gap-1"><Flame size={9}/> {j.boost_tier}</span>}
                  <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full ${j.status==="open"?"bg-[#0A0A0A] text-white":"bg-[#F2F2F0] text-[#4A4A4A]"}`}>{j.status}</span>
                </div>
                <p className="text-sm text-[#4A4A4A] mt-1">
                  {j.category} · {employmentLabel(j.employment_type)} · {j.location || "Remote"} · <span className="font-medium text-[#0A0A0A]">{formatSalary(j)}</span>
                </p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-sm font-medium">{j.application_count} applicant{j.application_count===1?"":"s"}</p>
                <p className="text-xs text-[#8A8A8A]">{new Date(j.created_at).toLocaleDateString()}</p>
              </div>
              {open === j.id ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}
            </button>

            {open === j.id && (
              <div className="border-t border-[#E5E5E3] bg-[#FAFAF8] px-3 sm:px-5 py-4" data-testid={`mine-apps-${j.id}`}>
                {!appsByJob[j.id] && <p className="text-sm text-[#8A8A8A] flex items-center gap-1.5"><Loader2 size={12} className="animate-spin"/> Loading applicants…</p>}
                {appsByJob[j.id]?.length === 0 && <p className="text-sm text-[#8A8A8A]">No applicants yet — share the link or boost the post.</p>}
                {(appsByJob[j.id] || []).map(a => (
                  <ApplicantRow key={a.id} a={a} setStatus={(s)=>setStatus(a.id, s, j.id)}/>
                ))}
                <div className="mt-3 flex justify-end">
                  <Link to={`/jobs/${j.id}`} className="btn-ghost text-sm" data-testid={`mine-view-${j.id}`}>
                    <Eye size={13}/> View live page
                  </Link>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ApplicantRow({ a, setStatus }) {
  const [expanded, setExpanded] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const isHired = a.status === "hired";
  const isRejected = a.status === "rejected";
  return (
    <div className="card p-4 mb-2" data-testid={`applicant-row-${a.id}`}>
      <div className="flex items-start gap-3">
        <img src={a.applicant_avatar} alt="" className="h-10 w-10 rounded-full object-cover bg-[#E5E5E3]"/>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Link to={`/u/${a.applicant_id}`} className="font-medium hover:underline" data-testid={`applicant-name-${a.id}`}>{a.applicant_name}</Link>
            <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full
              ${a.status === "hired" ? "bg-[#0A0A0A] text-white" :
                a.status === "rejected" ? "bg-[#FBE8E5] text-[#B84B4B]" :
                a.status === "shortlisted" ? "bg-[#EEF3FF] text-[#0057FF]" :
                "bg-[#F2F2F0] text-[#4A4A4A]"}`}>
              {a.status}
            </span>
          </div>
          <p className="text-xs text-[#8A8A8A]">{new Date(a.created_at).toLocaleDateString()}</p>
          <p className={`text-sm text-[#0A0A0A] mt-2 ${expanded ? "" : "line-clamp-3"}`} data-testid={`applicant-note-${a.id}`}>{a.cover_note}</p>
          {a.answers?.length > 0 && expanded && (
            <ul className="mt-2 space-y-1.5">
              {a.answers.map((ans, i) => ans && (
                <li key={i} className="text-sm text-[#4A4A4A]">
                  <span className="font-medium text-[#0A0A0A]">A{i+1}:</span> {ans}
                </li>
              ))}
            </ul>
          )}
          <button onClick={()=>setExpanded(e=>!e)} className="text-xs text-[#4A4A4A] hover:text-[#0A0A0A] mt-1.5">
            {expanded ? "Collapse" : "Show full"}
          </button>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-3">
        <button
          onClick={()=>setChatOpen(o=>!o)}
          className={`text-xs ${chatOpen ? "btn-primary !py-1.5 !px-3" : "btn-ghost"}`}
          data-testid={`applicant-chat-toggle-${a.id}`}
        >
          <MessageCircle size={12}/> {chatOpen ? "Close chat" : "Message"}
        </button>
        {!isHired && (
          <>
            <button onClick={()=>setStatus("shortlisted")} className="btn-ghost text-xs" data-testid={`applicant-shortlist-${a.id}`}><Star size={12}/> Shortlist</button>
            <button onClick={()=>setStatus("hired")} className="btn-primary !py-1.5 !px-3 text-xs" data-testid={`applicant-hire-${a.id}`}><CheckCircle2 size={12}/> Hire</button>
          </>
        )}
        {!isRejected && !isHired && (
          <button onClick={()=>setStatus("rejected")} className="btn-ghost text-xs text-[#B84B4B]" data-testid={`applicant-reject-${a.id}`}><XCircle size={12}/> Reject</button>
        )}
      </div>
      {chatOpen && (
        <div className="mt-3" data-testid={`applicant-chat-${a.id}`}>
          <ApplicationChat appId={a.id} compact />
        </div>
      )}
    </div>
  );
}
